/**
 * Contracts tab — plan §11 item 5 (contracts-plan.md; §13.7 WP-62b, R26/R36),
 * replacing WP-03's `PlaceholderScreen`. Clause list with flags and
 * similarity matches, position shown beside each flagged clause, apply
 * clause language from a previous study, decisions, redline export.
 *
 * VERSION SELECTION (session 4 DECISION a): this tab takes its version from
 * `?versionId=` and NOTHING else — `ContractsApp.tsx` is a §13.12 hot file
 * this WP may not touch, so no route param was added. `useSearchParams`
 * (the `WorkspaceTab.tsx` precedent) reads it; an absent or non-uuid value
 * renders `VersionPicker`, never a doomed request. Picking a version calls
 * `setSearchParams`, the tab's only navigation.
 *
 * REALTIME (§16.3, session 4 DECISION g): nothing here subscribes — prod
 * runs SSE and no channel exists for this module yet. This tab refetches
 * after every write and carries no poll at all.
 *
 * `ContractsTab` keeps its filename, its named export and its default
 * export, so `tabs/index.ts`'s existing `export { ContractsTab } from
 * './contracts/ContractsTab';` needs no edit.
 */
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FileSignature, Loader2 } from 'lucide-react';
import PageHeader from '../../../ui/PageHeader';
import { ToastProvider, useToast } from '../../../ui/Toast';
import { primaryBtnCls, secondaryBtnCls } from '../../../ui/styles';
import {
  decideClauseReview,
  describeClausesApiError,
  listVersionClauses,
  reviewVersionClauses,
  type ClauseDecision,
  type ListClausesResult,
  type ReviewSummary,
} from '../../api/clauses';
import { CONTRACTS_TABS } from '../config';
import { VersionPicker } from './VersionPicker';
import { ClauseList } from './ClauseList';
import { CarryFromDialog } from './CarryFromDialog';
import { RedlineExportCard } from './RedlineExportCard';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const tabConfig = CONTRACTS_TABS.find((t) => t.key === 'contracts')!;

function ContractsTabInner() {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const rawVersionId = searchParams.get('versionId');
  const versionId = rawVersionId && UUID_RE.test(rawVersionId) ? rawVersionId : null;

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [data, setData] = useState<ListClausesResult | null>(null);
  const [reviewing, setReviewing] = useState(false);
  const [decidingId, setDecidingId] = useState<string | null>(null);
  const [carryOpen, setCarryOpen] = useState(false);
  // FACTS 4: `screen_fail_payment.verdict` and `coverage.uncompared[]` live
  // only on the POST /review response, never on the clauses GET — kept here
  // so the verdict line and the "not compared (n)" disclosure (G9: nothing
  // unread is silent) can render below the PageHeader after a review runs.
  const [reviewSummary, setReviewSummary] = useState<ReviewSummary | null>(null);

  const load = useCallback(async (id: string) => {
    setLoading(true);
    setLoadError(null);
    try {
      const result = await listVersionClauses(id);
      setData(result);
    } catch (err) {
      setLoadError(describeClausesApiError('GET', `/api/contracts/versions/${id}/clauses`, err));
    } finally {
      setLoading(false);
    }
  }, []);

  // `reviewSummary` is cleared here — on a VERSION CHANGE — rather than
  // inside `load()` itself: `load()` also runs as the post-write refetch
  // from `handleRunReview` and `handleDecide`, and `handleRunReview` sets
  // `reviewSummary` from the POST /review response immediately before
  // calling `load(versionId)` to refresh the clause list. Clearing inside
  // `load()` would race that very set-then-refetch sequence (both are
  // synchronous state updates batched into the same render) and wipe the
  // summary before it is ever shown — a fix applied after review found this
  // exact defect in the naive "clear it in load()" version of this change.
  useEffect(() => {
    setReviewSummary(null);
    if (versionId) {
      void load(versionId);
    } else {
      setData(null);
      setLoadError(null);
    }
  }, [versionId, load]);

  async function handleRunReview() {
    if (!versionId) return;
    setReviewing(true);
    try {
      const summary = await reviewVersionClauses(versionId);
      setReviewSummary(summary);
      toast('Review complete.', { type: 'success' });
      await load(versionId);
    } catch (err) {
      const message = describeClausesApiError('POST', `/api/contracts/versions/${versionId}/review`, err);
      setLoadError(message);
      toast(message, { type: 'error' });
    } finally {
      setReviewing(false);
    }
  }

  async function handleDecide(reviewId: string, decision: ClauseDecision) {
    if (!versionId) return;
    setDecidingId(reviewId);
    try {
      await decideClauseReview(reviewId, decision);
      // FACTS 7: no optimistic write — refetch so the screen never disagrees with the server.
      await load(versionId);
    } catch (err) {
      toast(describeClausesApiError('PUT', `/api/contracts/clauses/${reviewId}/decision`, err), { type: 'error' });
    } finally {
      setDecidingId(null);
    }
  }

  if (!versionId) {
    return (
      <VersionPicker
        invalid={Boolean(rawVersionId)}
        onSelect={(id) => setSearchParams({ versionId: id })}
      />
    );
  }

  return (
    <div data-testid="ctr-tab" className="mx-auto w-full max-w-[1700px] space-y-4 p-4 sm:p-6 lg:p-8">
      <PageHeader
        title="Contracts"
        icon={FileSignature}
        subtitle={tabConfig.description}
        actions={
          <>
            {data && data.reviewed_count === 0 && (
              <button
                type="button"
                data-testid="ctr-run-review"
                className={primaryBtnCls}
                disabled={reviewing}
                onClick={handleRunReview}
              >
                {reviewing ? 'Reviewing…' : 'Run review'}
              </button>
            )}
            <button type="button" data-testid="ctr-carry-open" className={secondaryBtnCls} onClick={() => setCarryOpen(true)}>
              Apply clause language…
            </button>
          </>
        }
      />

      {reviewSummary && (
        <p
          data-testid="ctr-screen-fail-verdict"
          title="from POST /versions/:id/review (R26)"
          className="text-sm text-gray-700 dark:text-gray-300"
        >
          {reviewSummary.screen_fail_payment.verdict ?? 'no verdict returned'}
        </p>
      )}

      {reviewSummary && (
        <details data-testid="ctr-uncompared">
          <summary className="cursor-pointer text-xs text-gray-500 dark:text-gray-400 tabular-nums">
            not compared ({reviewSummary.coverage.uncompared.length})
          </summary>
          <ul className="text-xs text-gray-600 dark:text-gray-300">
            {reviewSummary.coverage.uncompared.map((u, i) => (
              <li key={i}>{typeof u === 'string' ? u : JSON.stringify(u)}</li>
            ))}
          </ul>
        </details>
      )}

      {loadError && (
        <p data-testid="ctr-error" className="text-sm text-red-600 dark:text-red-400">
          {loadError}
        </p>
      )}

      {loading && !data && (
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <Loader2 size={16} className="animate-spin" /> Loading clauses…
        </div>
      )}

      {data && (
        <>
          <ClauseList data={data} decidingId={decidingId} onDecide={handleDecide} />
          <RedlineExportCard versionId={versionId} />
        </>
      )}

      <CarryFromDialog
        isOpen={carryOpen}
        onClose={() => setCarryOpen(false)}
        targetVersionId={versionId}
        onRunReview={handleRunReview}
        onCarried={() => load(versionId)}
      />
    </div>
  );
}

export function ContractsTab() {
  return (
    <ToastProvider>
      <ContractsTabInner />
    </ToastProvider>
  );
}

export default ContractsTab;
